import React from "react";
import { GetServerSideProps, NextPage } from "next";
// import LeftAuthPanel from "../../components/LeftAuthPanel";
import BrimbleLogo from "../../Icons/BrimbleLogo";
import TextField from "../../components/Micro/TextField";
import Button from "../../components/Micro/Button";
import { Formik } from "formik";
import { toast } from "sonner";
import { register, userLookup } from "../../backend/auth";
import { useRouter } from "next/router";
import { useStoreContext } from "../../context";
import { Actions } from "../../context/reducer";
import config from "../../config";
import validation from "../../utils/validation";
import { handleRequestError } from "../../backend";
import Link from "next/link";
import { LookupType, RegisterDataType } from "../../types/auth.type";
import CheckBoxComponent from "../../components/Micro/CheckBoxComponent";
import Seo from "../../components/Micro/Seo";
import AuthLayout from "@/layouts/AuthLayout";

const Register: NextPage = () => {
  const router = useRouter();
  const [isChecked, setIsChecked] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<LookupType>({
    email: "",
    username: "",
  });
  const { dispatch } = useStoreContext();

  const handleUserLookup = (data: LookupType) => {
    userLookup(data)
      .then(() => {
        const tmp: any = { ...error };
        tmp[Object.keys(data)[0]] = "";
        setError(tmp);
      })
      .catch((res: any) => {
        if (res?.status === 400) {
          const tmp: any = { ...error };
          tmp[Object.keys(data)[0]] = res.data.message;
          setError(tmp);
        }
      });
  };

  const handleRegister = (
    registrationPayload: RegisterDataType,
    resetForm: () => void,
  ) => {
    if (!Object.values(error).join("").trim()) {
      setLoading(true);
      register(registrationPayload)
        .then(({ data }: any) => {
          dispatch({ type: Actions.AUTHENTICATE, payload: data.data });
          resetForm();
          toast.success("Account created successfully!", { duration: 10000 });

          router.push(
            `/login/verification?email=${registrationPayload.email}&from=register`,
          );
        })
        .catch(handleRequestError)
        .finally(() => setLoading(false));
    }
  };

  return (
    <>
      <Seo
        title="Registration"
        description="Create an account to start using Brimble"
      />

      <AuthLayout>
        <Formik
          initialValues={{
            email: "",
            username: "",
            first_name: "",
            last_name: "",
            company: "",
          }}
          validationSchema={validation.register}
          onSubmit={(data: RegisterDataType, { resetForm }) =>
            handleRegister(data, resetForm)
          }
        >
          {({
            errors,
            values,
            touched,
            handleSubmit,
            handleChange,
            handleBlur,
          }) => (
            <>
              <div className="flex items-center justify-between w-full">
                <div className="space-y-3 w-4/5">
                  <h1 className="text-4xl font-extrabold md:text-[40px] text-primary-dark dark:text-white">
                    Ship your{" "}
                    <span className="text-blue-600 italic">Applications</span>{" "}
                    fast and easy
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 text-lg w-4/5 md:w-2/3 lg:w-2/3 xl:1/2">
                    Create an account to start deploying on brimble.
                  </p>
                </div>
                <Link href={"https://brimble.io"} passHref>
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-light">
                    <BrimbleLogo />
                  </div>
                </Link>
              </div>
              <div className="w-full py-5 space-y-2">
                <div className="grid w-full grid-cols-2 gap-x-3 gap-y-2">
                  <TextField
                    value={values["first_name"]}
                    error={Boolean(errors["first_name"])}
                    helperText={touched["first_name"] && errors["first_name"]}
                    touched={touched["first_name"]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="text"
                    name="first_name"
                    placeholder="Armstrong"
                    label={"First Name *"}
                    className="!input-md border-none dark:bg-theme-color-light"
                  />
                  <TextField
                    value={values["last_name"]}
                    error={Boolean(errors["last_name"])}
                    helperText={touched["last_name"] && errors["last_name"]}
                    touched={touched["last_name"]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="text"
                    name="last_name"
                    placeholder="Niel"
                    label={
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Last Name{" "}
                        <span className="text-gray-500 italic">(Optional)</span>
                      </p>
                    }
                    className="!input-md border-none dark:bg-theme-color-light"
                  />
                  <TextField
                    value={values["email"]}
                    error={Boolean(errors["email"]) || !!error.email}
                    helperText={
                      (touched["email"] && errors["email"]) || error.email
                    }
                    touched={touched["email"]}
                    onChange={handleChange}
                    onBlur={(event: any) => {
                      handleBlur(event);
                      if (values.email) {
                        handleUserLookup({ email: values.email });
                      }
                    }}
                    type="email"
                    name="email"
                    placeholder="armstrong@gmail.com"
                    label={"Email *"}
                    containerClass={"col-span-2"}
                    className="!input-md border-none dark:bg-theme-color-light"
                  />
                  <TextField
                    value={values["username"]}
                    error={Boolean(errors["username"]) || !!error.username}
                    helperText={
                      (touched["username"] && errors["username"]) ||
                      error.username
                    }
                    touched={touched["username"]}
                    onChange={handleChange}
                    onBlur={(event: any) => {
                      handleBlur(event);
                      if (values.username) {
                        handleUserLookup({ username: values.username });
                      }
                    }}
                    type="text"
                    name="username"
                    placeholder="username"
                    label={"Username *"}
                    className="!input-md border-none dark:bg-theme-color-light"
                  />
                  <TextField
                    value={values["company"]}
                    error={Boolean(errors["company"])}
                    helperText={touched["company"] && errors["company"]}
                    touched={touched["company"]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    type="text"
                    name="company"
                    placeholder="company"
                    label={
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Company{" "}
                        <span className="text-gray-500 italic">(Optional)</span>
                      </p>
                    }
                    className="!input-md border-none dark:bg-theme-color-light"
                  />
                </div>

                <div className="flex items-center space-x-2 !my-5 text-[12px] font-mono">
                  <CheckBoxComponent
                    checked={isChecked}
                    onChange={(e: React.FormEvent<HTMLInputElement>) =>
                      setIsChecked(e.currentTarget.checked)
                    }
                  />
                  <span>
                    I agree to the terms and conditions of{" "}
                    <Link href="#">
                      <span className="text-primary cursor-pointer">
                        Brimble
                      </span>
                    </Link>
                  </span>
                </div>

                <Button
                  loading={loading}
                  type="submit"
                  onClick={() => handleSubmit()}
                  className="text-md btn-block btn-md"
                  disabled={isChecked === false}
                >
                  continue
                </Button>
                <button
                  onClick={() => router.push("/register")}
                  className="mt-5 text-[14px] font-medium capitalize btn-link text-primary dark:text-gray-400 btn btn-primary btn-block btn-lg no-underline"
                >
                  Use other Registration options
                </button>
              </div>
            </>
          )}
        </Formik>
      </AuthLayout>
    </>
  );
};

export default Register;

export const AuthLabel = () => (
  <span className="flex items-center space-x-2 text-sm font-semibold text-black label-text dark:text-white">
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 15.75V14.25C15 12.5931 13.6569 11.25 12 11.25H6C4.34315 11.25 3 12.5931 3 14.25V15.75"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <span>Email Address</span>
  </span>
);

export const VerifyLabel = () => (
  <span className="flex items-center space-x-2 text-sm font-semibold text-black label-text dark:text-white">
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 15.75V14.25C15 12.5931 13.6569 11.25 12 11.25H6C4.34315 11.25 3 12.5931 3 14.25V15.75"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <span>OTP Code</span>
  </span>
);

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  if (req.cookies[config.ACCESS_TOKEN]) {
    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    };
  } else {
    return {
      props: {},
    };
  }
};
